<script lang="ts" setup>
  /**
   * Phone number — 03-components/22-phone-number · OSCloud DS 1.1.152
   */
  import { computed, ref, useId, watch } from 'vue'
  import BfSelect from '@/components/ui/bahnhof/BfSelect.vue'
  import type { BfSelectOption } from '@/components/ui/bahnhof/BfSelectTypes'
  import { useRegisterStore } from '@/modules/register/store'
  import type { CommonErrorI } from '@/typescript/types/abstracts/errors/ErrorHandlerTypes'
  import type { OptionItemI } from '@/typescript/types/ui-components/SelectItem'
  import { isRestrictedCountry } from '@/utils/restrictedCountries'

  const props = withDefaults(
    defineProps<{
      code: string
      number: string
      errorCode?: CommonErrorI | string | null
      errorNumber?: CommonErrorI | string | null
      disabled?: boolean
      label?: string
      /** Sentence-case label, the Form field (15) --plain variant — as BfInput's prop. */
      plainLabel?: boolean
      hint?: string
      name?: string
      placeholder?: string
      /** The number input's id — for a page that moves focus to it. Generated when omitted. */
      id?: string
    }>(),
    {
      errorCode: '',
      errorNumber: '',
      disabled: false,
      label: 'Phone number',
      plainLabel: false,
      hint: 'Used for urgent service notifications.',
      name: 'phonenumber',
      placeholder: '',
      id: undefined,
    },
  )

  const emit = defineEmits<{
    (e: 'update:code', value: string): void
    (e: 'update:number', value: string): void
    (e: 'change'): void
    (e: 'input'): void
    (e: 'clear-error-code'): void
    (e: 'clear-error-number'): void
  }>()

  const registerStore = useRegisterStore()

  // The store's option shape predates the design system's; map it onto the
  // select's own — inputTitle is the closed-control text, rightLabel the row's
  // trailing label. Dial codes repeat (+1, +44, +358), so an option is keyed by
  // its country, as the card's own options are, and the code is only what it
  // displays: keyed by code, every country sharing it read as selected. The
  // same country arriving twice from the API is listed once.
  const dialOptions = computed(() => {
    const byKey = new Map<string, { code: string; option: BfSelectOption }>()
    for (const item of registerStore.countriesPhoneCodesList as OptionItemI[]) {
      const key = `${item.title}|${item.value}`
      if (byKey.has(key)) continue
      byKey.set(key, {
        code: `${item.value}`,
        option: {
          label: item.title,
          value: key,
          display: item.inputTitle,
          trailing: item.rightLabel,
          blocked: isRestrictedCountry(null, item.title),
        },
      })
    }
    return byKey
  })
  const codeOptions = computed(() => [...dialOptions.value.values()].map(entry => entry.option))
  const codeOf = (key: string) => dialOptions.value.get(key)?.code ?? ''

  // Only the code is stored, so the country is remembered here: the one picked,
  // for as long as the code still belongs to it, otherwise the first country
  // with the code (an initial value, or a code set from outside).
  const pickedKey = ref<string | null>(null)
  const selectedKey = computed(() => {
    const code = `${props.code}`
    if (pickedKey.value && codeOf(pickedKey.value) === code) return pickedKey.value
    for (const [key, entry] of dialOptions.value) if (entry.code === code) return key
    return null
  })

  // Whether the dial code was picked for a sanctioned country. Judged on the
  // country, never the code alone: +7 is Kazakhstan's as well as Russia's.
  // The parent binds v-model:restricted, shows RestrictedCountryNotice and
  // holds its submit while this is true.
  const restricted = defineModel<boolean>('restricted', { default: false })
  watch(
    () => !!selectedKey.value && !!dialOptions.value.get(selectedKey.value)?.option.blocked,
    value => {
      restricted.value = value
    },
    { immediate: true },
  )

  const onCountryUpdate = (key: string | number) => {
    pickedKey.value = `${key}`
    onCodeUpdate(codeOf(`${key}`))
  }

  const errorText = (error: CommonErrorI | string | null | undefined): string => {
    if (!error) return ''
    return typeof error === 'string' ? error : (error.message ?? '')
  }

  const codeErrorText = computed(() => errorText(props.errorCode))
  const numberErrorText = computed(() => errorText(props.errorNumber))
  const messageText = computed(() => numberErrorText.value || codeErrorText.value || props.hint)
  const hasError = computed(() => !!(numberErrorText.value || codeErrorText.value))

  const generatedId = useId()
  const inputId = computed(() => props.id ?? generatedId)

  const NUMBER_RE = /^\+\d+\.(.+)$/

  const displayNumber = computed({
    get: () => {
      if (!props.number) return ''
      const match = props.number.match(NUMBER_RE)
      return match ? match[1] : props.number
    },
    set: (value: string) => {
      emit('update:number', props.code && value ? `+${props.code}.${value}` : value)
    },
  })

  const onNumberInput = (event: Event) => {
    displayNumber.value = (event.target as HTMLInputElement).value
    emit('clear-error-number')
    emit('input')
  }

  const onCodeUpdate = (nextCode: string) => {
    emit('update:code', nextCode)
    const match = props.number?.match(NUMBER_RE)
    const numberPart = match ? match[1] : props.number
    if (nextCode && numberPart) {
      emit('update:number', `+${nextCode}.${numberPart}`)
    }
    emit('clear-error-code')
    emit('change')
  }
</script>

<template>
  <!-- A mix on .form-field whose control slot holds the dial-code .select-field
       beside the number input. Both halves take the foundation's 44px default —
       one baseline, no size class. -->
  <div class="form-field phone-number-field">
    <!-- The `label` slot carries a label that is more than a string — a required field's
         `.required-marker`. It replaces the prop's text; the prop stays the plain-string path. -->
    <label class="form-field__label" :class="{ 'form-field__label--plain': plainLabel }" :for="inputId"
      ><slot name="label">{{ label }}</slot></label
    >
    <div class="form-field__control phone-number-field__row">
      <BfSelect
        composed
        searchable
        class="phone-number-field__code"
        :model-value="selectedKey"
        :options="codeOptions"
        :disabled="disabled"
        :error="codeErrorText"
        search-placeholder="Search country"
        aria-label="Country dial code"
        @update:model-value="onCountryUpdate"
      />
      <input
        :id="inputId"
        class="form-field__input phone-number-field__number"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        :name="name"
        :value="displayNumber"
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-invalid="numberErrorText ? 'true' : 'false'"
        @input="onNumberInput"
        @change="emit('change')"
      />
    </div>
    <div
      v-if="messageText"
      class="form-field__message"
      :role="hasError ? 'alert' : undefined"
      :data-test-id="hasError ? 'field-error' : undefined"
    >
      {{ messageText }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* The foundation, for the elements this component renders. */
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    &__label--plain {
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    > .form-field__label {
      margin-bottom: var(--bf-form-label-gap);
    }

    &__control {
      position: relative;
    }

    &__input {
      width: 100%;
      font-family: var(--bf-font-body);
      font-size: 15px;
      height: var(--bf-form-control-height);
      padding: 0 14px;
      color: var(--bf-fg-1);
      background: var(--bf-form-control-surface);
      border: 1px solid var(--bf-form-control-hairline);
      border-radius: var(--bf-form-control-radius);
      transition:
        border-color var(--bf-dur-fast) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease),
        background var(--bf-dur-fast) var(--bf-ease);

      &::placeholder {
        color: var(--bf-form-control-placeholder);
      }

      &:hover:not(:focus):not(:disabled) {
        border-color: var(--bf-form-control-hairline-hover);
      }

      &:focus-visible {
        outline: none;
        border-color: var(--bf-form-control-hairline-focus);
        box-shadow: var(--bf-form-focus-ring);
        background: var(--bf-form-control-surface-focus);
      }

      &:disabled {
        opacity: var(--bf-form-disabled-opacity);
        cursor: not-allowed;
      }

      &[aria-invalid='true'] {
        border-color: var(--bf-form-invalid-hairline);
        background: var(--bf-form-invalid-surface);
      }
    }

    &__message {
      color: var(--bf-fg-2);
      /* Stated, not inherited, as in BfInput: the app's body still carries the legacy family. */
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }

  /* This block is the row; everything else is .form-field and .select-field. */
  .phone-number-field {
    --phone-number-field-code-width: 160px;
    container-type: inline-size;

    /* The card sets the menu's floor on __code, but BfSelect is inheritAttrs: false, so a class
       on it lands on its input — never an ancestor of the menu. The row is. Country names need
       more room than the code column gives the menu. */
    &__row {
      --select-field-menu-min-width: 280px;
      display: grid;
      grid-template-columns: var(--phone-number-field-code-width) 1fr;
      gap: var(--bf-sp-2);
      align-items: start;
    }
  }

  /* The code and the number are ONE value: two columns at every width, never stacked. On a
     narrow field the code column narrows instead — 100px fits the widest dial code (+380) with
     the select's padding and caret. Queried on the field, not the viewport, and set on __row
     because the field is the container and cannot query itself. */
  @container (max-width: 420px) {
    .phone-number-field__row {
      --phone-number-field-code-width: 100px;
    }
  }
</style>
